import type { NostrEvent } from '@nostrify/nostrify';
import { useNostr } from '@nostrify/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { HIDDEN_POST_IDS, HIDDEN_POST_PHRASES } from '@/lib/showcase';

const PAGE_SIZE = 20;

/** A top-level text note (kind 1) that is not a reply to another note. */
function isTopLevelPost(event: NostrEvent): boolean {
  if (event.kind !== 1) return false;
  // Exclude replies: any `e` tag means this note references another note.
  const hasReply = event.tags.some(([name]) => name === 'e');
  return !hasReply;
}

/** Whether a post is explicitly hidden by id or by a content phrase. */
function isHidden(event: NostrEvent): boolean {
  if (HIDDEN_POST_IDS.has(event.id)) return true;
  const content = event.content.toLowerCase();
  return HIDDEN_POST_PHRASES.some((phrase) =>
    content.includes(phrase.toLowerCase()),
  );
}

/**
 * Fetches top-level text notes authored by the given pubkey, paginated by
 * `created_at` using infinite scroll.
 */
export function useShowcasePosts(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useInfiniteQuery({
    queryKey: ['showcase-posts', pubkey ?? ''],
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam, signal }) => {
      if (!pubkey) return [] as NostrEvent[];

      const events = await nostr.query(
        [
          {
            kinds: [1],
            authors: [pubkey],
            limit: PAGE_SIZE,
            ...(pageParam ? { until: pageParam } : {}),
          },
        ],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) },
      );

      // Deduplicate by id (the same event can arrive from multiple relays).
      const seen = new Set<string>();
      const unique = events.filter((e) => {
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        return true;
      });

      return unique
        .filter(isTopLevelPost)
        .filter((e) => !isHidden(e))
        .sort((a, b) => b.created_at - a.created_at);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length === 0) return undefined;
      // Use the oldest event's timestamp (minus 1) as the next cursor.
      const oldest = lastPage[lastPage.length - 1];
      return oldest.created_at - 1;
    },
    enabled: !!pubkey,
  });
}
